import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, throwError } from 'rxjs';
import { AbsService, AssetMetadataService, AssetService, S3Service, CreatePresignedUrlRequest } from '@dal/core';
import {
  CreateABSAsset,
  TemplateDetails,
  Details,
} from '../models/asset.metadata.model';
import { UtilService } from './util.service';
import { FormGroup, FormBuilder, FormArray, Validators } from '@angular/forms';
import * as ExifReader from 'exifreader';
import { SnackBarService } from './snack-bar.service';
import { IndexedDBService } from '../indexed-db/indexed-db.service';
import { UploadJob, UploadJobFiles } from '../indexed-db/indexed-db.interface';
import { JobStatus } from '@dal/async-upload';
import { UploadWorkerService } from './upload-worker.service';

// Internal interface for asset preview data
interface AssetPreviewData {
  file: File;
  blob: string;
  metadata?: {
    title?: string;
    [key: string]: unknown;
  };
}

// Internal interface for file metadata
interface FileMetadata {
  [key: string]: string | number | unknown;
}

// Internal interface for BMP metadata
interface BmpMetadata extends FileMetadata {
  width: number;
  height: number;
  bitDepth: number;
  fileSize: number;
}

// Internal interface for EXIF tag data
interface ExifTag {
  description?: string;
  value?: string | number;
}

// Internal interface for extracted metadata from files
interface ExtractedMetadata {
  title: string;
  description: string;
  type: string;
  asset_type: string;
  [key: string]: unknown; // Index signature to allow additional properties
}

@Injectable({
  providedIn: 'root',
})
export class UploadAssetService {
  private _currentTemplate: TemplateDetails;
  private _files: File[] = [];
  assetPayload: AssetPreviewData[] = [];
  asset_sources: Details[] = [];
  assetPreviewForm: FormGroup;
  metadataForm: FormGroup;
  assetIndex: number = 0;
  private assetIndexSubject = new BehaviorSubject<number>(this.assetIndex);
  assetIndex$ = this.assetIndexSubject.asObservable();
  private uploadedFiles: string[] = [];
  private uploadProgressSubject = new BehaviorSubject<number>(0);
  uploadProgress$ = this.uploadProgressSubject.asObservable();

  constructor(
    private absService: AbsService,
    private assetService: AssetService,
    private utilService: UtilService,
    private s3Service: S3Service,
    private fb: FormBuilder,
    private assetMetadataService: AssetMetadataService,
    private snackbarService: SnackBarService,
    private indexedDBService: IndexedDBService,
    private uploadWorkerService: UploadWorkerService
  ) {
    // Defer subscription to avoid constructor ordering issues
    setTimeout(() => {
      this.subscribeToJobCompletion();
    }, 0);
  }

  private subscribeToJobCompletion(): void {
    // Subscribe to job completion events to reset file cache
    this.uploadWorkerService.jobCompletion$.subscribe({
      next: (jobId) => {
        console.log('🎉 Job completed, resetting file cache for job:', jobId);
        this.resetFileCache();
      },
      error: (error) => {
        console.error('❌ Error in job completion subscription:', error);
      },
    });
  }

  get currentTemplate(): TemplateDetails {
    // const storedTemplate = sessionStorage.getItem('currentTemplate');
    // return storedTemplate ? JSON.parse(storedTemplate) : this._currentTemplate;
    return this._currentTemplate;
  }

  set currentTemplate(template: TemplateDetails) {
    // sessionStorage.setItem('currentTemplate', JSON.stringify(template));
    this._currentTemplate = template;
  }

  get files(): File[] {
    return this._files;
  }

  set files(files: File[]) {
    this._files.push(...files);
  }

  removeFile(index: number) {
    this._files.splice(index, 1);
    (<FormArray>this.assetPreviewForm.controls['files']).removeAt(index);
    this.assetPayload.splice(index, 1);
  }

  duplicateCheck(): Observable<File[]> {
    return forkJoin(
      this.files.map((file) => {
        const hashed = this.utilService.computeMd5Hash(file.name);
        return this.absService.duplicateCheck(hashed).pipe(
          switchMap((response) => {
            const uuid = response.result.asset.id;
            return this.assetMetadataService.getMetadataById(uuid).pipe(
              switchMap((asset) => {
                const isDuplicate =
                  this.currentTemplate.asset_source.name ===
                  asset.asset.asset_source.name;
                return of(isDuplicate ? file : null);
              })
            );
          }),
          catchError((error: { status?: number }) => {
            if (error.status === 404 || error.status === 500) {
              return of(null);
            }
            return throwError(() => error);
          })
        );
      })
    ).pipe(
      map((results: (File | null)[]) =>
        results.filter((file): file is File => file !== null)
      )
    );
  }

  initAssetPayload(metadata: { [key: string]: unknown }[] = []): void {
    this.assetPayload = this.files.map((file, index) => {
      return {
        file,
        blob: this.getImageUrl(file),
        metadata: metadata[index],
      };
    });
  }

  setAssetIndex(newIndex: number): void {
    this.assetIndex = newIndex;
    this.assetIndexSubject.next(this.assetIndex);
  }

  private getImageUrl(file: File): string {
    return URL.createObjectURL(file);
  }

  formInitialization(): void {
    this.metadataForm = this.fb.group({
      'asset-source': [
        this.getMetadataFieldsData('asset-source'),
        Validators.required,
      ],
      'rights-category': [
        this.getMetadataFieldsData('rights-category'),
        Validators.required,
      ],
      'adaptation-rights': [
        this.getMetadataFieldsData('adaptation-rights'),
        Validators.required,
      ],
      'business-unit-rights': [
        this.getMetadataFieldsData('business-unit-rights'),
      ],
      'credit-text': [this.getMetadataFieldsData('credit-text')],
      'model-release': [this.getMetadataFieldsData('model-release')],
      'location-release': [this.getMetadataFieldsData('location-release')],
      'usage-restriction': [this.getMetadataFieldsData('usage-restriction')],
      notes: [this.getMetadataFieldsData('notes')],
      'distribution-rights': [
        this.getMetadataFieldsData('distribution-rights'),
      ],
      'licensing-limitations': [
        this.getMetadataFieldsData('licensing-limitations'),
      ],
      'marketing-use': [this.getMetadataFieldsData('marketing-use')],
      gps: [this.getMetadataFieldsData('gps')],

      'provider-bu': [
        this.getMetadataFieldsData('provider-bu'),
        Validators.required,
      ],
      'asset-creator': [this.getMetadataFieldsData('asset-creator')],
      'retail-collection': [this.getMetadataFieldsData('retail-collection')],
      'volume-label': [this.getMetadataFieldsData('volume-label')],

      'media-type': [
        { value: this.currentTemplate.model, disabled: true },
        Validators.required,
      ],
      'image-type': [this.getMetadataFieldsData('image-type')],
      'video-type': [this.getMetadataFieldsData('video-type')],
      'audio-type': [this.getMetadataFieldsData('audio-type')],
      'image-color-or-bw': [this.getMetadataFieldsData('image-color-or-bw')],
      'video-color-or-bw': [this.getMetadataFieldsData('video-color-or-bw')],
      disability: [this.getMetadataFieldsData('disability')],
      'video-sound': [this.getMetadataFieldsData('video-sound')],
    });
    this.assetPreviewForm = this.fb.group({
      files: this.fb.array([]),
    });

    this.assetPayload
      .map((asset) => {
        return { ...asset, blob: this.getImageUrl(asset.file) };
      })
      .map((asset) => this.addItem(asset));
  }

  private addItem(value: AssetPreviewData): void {
    const files = this.assetPreviewForm.get('files') as FormArray;
    files.push(this.createItem(value));
  }

  private createItem({ file, blob, metadata }: AssetPreviewData): FormGroup {
    return this.fb.group({
      fileName: [file.name, Validators.required],
      title: [metadata?.title, Validators.required],
      src: [blob, Validators.required],
    });
  }

  getMetadataFieldsData(key: string): string | string[] | null {
    const field = this.currentTemplate?.fields?.find(
      (field) => field.metadata_type.key === key
    );
    if (field === undefined) return null;
    if (
      field?.metadata_type.metadata_field_type_id === 2 &&
      field?.metadata_type.multi_value
    ) {
      return field.default_value;
    }
    return field?.default_value[0] || '';
  }

  // upload calls
  uploadToS3(): Observable<string> {
    const fileNames = this.files.map((file) => file.name);
    // get selected asset source
    const selectedAssetSource = this.asset_sources.find(
      (source) => source.name === this.metadataForm.value['asset-source']
    )?.id;
    if (!selectedAssetSource) {
      throw new Error('No asset source selected');
    }
    const fileNamesWithSource: CreatePresignedUrlRequest[] = fileNames.map(
      (fileName: string) => {
        return {
          filename: fileName,
          asset_source_id: selectedAssetSource,
        };
      }
    );

    return this.s3Service.getS3PresignedUrl(fileNamesWithSource).pipe(
      switchMap((presignedUrls: { path: string }[]) => {
        const presignedUrlsPath = presignedUrls.map((url) => url.path);
        const assetData: CreateABSAsset[] =
          this.buildAssetData(presignedUrlsPath);
        // Set job data
        const jobData: UploadJob = this.setJobData(assetData);
        return this.indexedDBService.save(jobData).pipe(
          switchMap((result: UploadJob) => {
            console.log('✅ Job created:', result);
            const msg = `(${presignedUrlsPath.length}) file${
              presignedUrlsPath.length > 1 ? 's' : ''
            } queued for upload. Upload will begin shortly.`;
            this.snackbarService.open(msg);

            // Use the new UploadWorkerService instead of service worker
            console.log('🚀 Starting upload with new worker service...');
            // Convert promise to observable
            return new Observable<string>((observer) => {
              this.uploadWorkerService
                .startUpload(result.id, this.files, presignedUrlsPath)
                .then(() => {
                  console.log('✅ Upload initiated successfully');
                  observer.next(result.id);
                  observer.complete();
                })
                .catch((error) => {
                  console.error('❌ Failed to start upload:', error);
                  observer.error(error);
                });
            });
          })
        );
      })
    );
  }

  setJobData(jobData: CreateABSAsset[]): UploadJob {
    // Prepare files array
    const jobFiles: UploadJobFiles[] = this.files.map((file: File) => ({
      name: file.name,
      type: file.type,
      size: file.size,
      lastModified: file.lastModified,
      data: file, // File is already a Blob
    }));

    // Generate job metadata
    const now = Date.now();
    const job: UploadJob = {
      id: this.utilService.generateUuid(),
      userId: '1',
      status: JobStatus.PENDING,
      createdAt: now,
      retries: 0,
      files: jobFiles,
      data: jobData,
    };
    return job;
  }

  triggerOnSuccessfullUploads(jobID: string): Observable<UploadJob> {
    return this.indexedDBService.get(jobID).pipe(
      switchMap((jobData: UploadJob) => {
        return this.assetService.createAssetBulk(jobData.data).pipe(
          switchMap(() => {
            return this.indexedDBService.update({
              ...jobData,
              completion: 100,
              status: JobStatus.COMPLETE,
            });
          })
        );
      })
    );
  }

  // Function to build asset payload based on the uploaded files and form data
  private buildAssetData(s3Paths: string[]): CreateABSAsset[] {
    const selectedAssetSourceId = this.asset_sources.find(
      (source) => source.name === this.metadataForm.value['asset-source']
    )?.id;
    if (!selectedAssetSourceId) {
      throw new Error('No asset source selected for asset data');
    }

    return this.assetPayload.map((asset, index: number) => ({
      title: this.assetPreviewForm.value.files[index].title,
      filename: asset.file.name,
      asset_source_id: selectedAssetSourceId,
      asset_type_id: this.getAssetTypeId(index),
      gps_id: this.metadataForm.value['gps'],
      s3_path: s3Paths[index],
      metadata: this.setMetadata(),
    }));
  }

  private setMetadata(): NonNullable<CreateABSAsset['metadata']> {
    const formValues = Object.keys(this.metadataForm.value)
      .filter(
        (key) =>
          this.metadataForm.value[key] !== null &&
          this.metadataForm.value[key] !== 'gps'
      )
      .map((key) => ({
        key: key,
        value: Array.isArray(this.metadataForm.value[key])
          ? this.metadataForm.value[key]
          : [this.metadataForm.value[key]],
      }));
    const valuesFromTemplate = this.currentTemplate.fields
      .filter(
        (field) =>
          !formValues.find((value) => value.key === field.metadata_type.key)
      )
      .map((field) => {
        return {
          key: field.metadata_type.key,
          value: [...field.default_value],
        };
      });
    return [...formValues, ...valuesFromTemplate];
  }

  private getAssetTypeId(index: number): number {
    const fileType = this.files[index].type;
    if (fileType.includes('image')) {
      return 1;
    } else if (fileType.includes('video')) {
      return 2;
    } else if (fileType.includes('audio')) {
      return 3;
    } else {
      return 4; // Default or unknown type
    }
  }

  extractedTagsMetadata(): Promise<ExtractedMetadata[]> {
    return Promise.all(
      this.files.map(async (file) => {
        try {
          if (file.type.toLowerCase().includes('image')) {
            const tags = await this.readFileMetadata(file);
            const tagData =
              Array.isArray(tags) && tags.length > 0
                ? (tags[0] as { [key: string]: ExifTag })
                : {};
            return {
              title: tagData['Headline']?.description || '',
              description: tagData['description']?.description || '',
              type: tagData['FileType']?.description || '',
              asset_type: tagData['format']?.description || '',
            };
          } else {
            return {
              title: '',
              description: '',
              asset_type: '',
              type: file.type,
            };
          }
        } catch (error) {
          return {
            title: '',
            description: '',
            asset_type: '',
            type: file.type,
          };
        }
      })
    );
  }

  readFileMetadata(file: File): Promise<FileMetadata[]> {
    const promises: Promise<FileMetadata>[] = [];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    const promise = new Promise<FileMetadata>((resolve, reject) => {
      const reader = new FileReader();

      if (fileExtension === 'bmp') {
        reader.onload = (event) => {
          if (event.target) {
            const arrayBuffer = event.target.result as ArrayBuffer;
            const dataView = new DataView(arrayBuffer);

            // Extract BMP metadata from header
            const width = dataView.getInt32(18, true);
            const height = dataView.getInt32(22, true);
            const bitDepth = dataView.getUint16(28, true);
            const fileSize = dataView.getUint32(2, true);

            const bmpMetadata: BmpMetadata = {
              width,
              height,
              bitDepth,
              fileSize,
            };
            resolve(bmpMetadata);
          }
        };
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
      } else {
        reader.onload = (event: ProgressEvent<FileReader>) => {
          if (event.target) {
            try {
              const tags = ExifReader.load(event.target.result as ArrayBuffer);
              resolve(tags);
            } catch (error) {
              reject(error);
            }
          }
        };
        reader.onerror = (error) => reject(error);
        reader.readAsArrayBuffer(file);
      }
    });

    promises.push(promise);
    return Promise.all(promises);
  }

  resetFileCache(): void {
    this.assetPayload = [];
    this._files = [];
    this.assetIndex = 0;
    this.assetIndexSubject.next(this.assetIndex);
  }
}

